import React, { useEffect, useMemo, useRef, useState } from "react";
import { message, Select, Tooltip } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import {
  Brain,
  CheckCircle2,
  ChevronRight,
  CircleOff,
  ClipboardList,
  Database,
  Eye,
  FileSearch,
  FileText,
  GitGraph,
  Quote,
  Terminal,
} from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import type {
  GroundingArtifact,
  GroundingSourceCategory,
  GroundingSourceRecord,
  GroundingRationaleRecord,
  GroundingReferenceKind,
  GroundingReferenceTarget,
  ReferenceSectionOption,
} from "@/types/grounding";
import FileTypeIcon, { hasMappedFileTypeIcon } from "@/components/ui/FileTypeIcon";
import { useEnvironment } from "@/context/EnvironmentContext";
import { useAuth } from "@/context/AuthContext";
import {
  buildGroundingReferenceBlocks,
  getGroundingReferenceDisplayId,
  preprocessGroundingMarkerRuns,
} from "@/utility/groundingMarkers";
import { getGroundingMarkdownComponents } from "@/components/ui/GroundingMarkdownLink";
import {
  prepareGeneratedContentPreview,
} from "@/utility/prepareGeneratedContentPreview";
import { GeneratedContentPreview } from "@/components/GeneratedContentPreview";
import {
  openSourceViewerDialog,
  storeSourceViewerPayload,
  type SourceViewerPayload,
} from "@/utility/sourceViewerStorage";
import { closeActiveViewerDialog } from "@/utility/officeDialogManager";
import { beginViewerOpenStatus, sourceDocumentDisplayName } from "@/utility/viewerClosingStatus";
import { GroundingMarkdown } from "@/components/ui/GroundingMarkdown";
import type { FontStyleSettings } from "@/lib/types";
import {
  absenceSearchSummary,
  absenceSearchTerms,
  isAbsenceSearchSource,
  isUnresolvedFileSource,
  sourceExcerptDisplayText,
  sourceLocatorDisplayText,
  type AbsenceSearchSummary,
} from "@/utility/groundingSourcePresentation";
import {
  getStoredRationaleEvidenceDetailMode,
  isRationaleEvidenceDetailMode,
  RATIONALE_EVIDENCE_DETAIL_CHANGED_EVENT,
  type RationaleEvidenceDetailMode,
  getSourceFileExtension,
  shouldShowSourceInLessRationaleDetail,
} from "@/utility/rationaleSettings";
import type { GraphViewController } from "@/utility/graphViewStorage";
import "./ReferencePage.css";
import { getTodoTextComponents } from "@/utility/todoMarkdownComponents";

interface ReferencePageProps {
  groundingByTaskId: Record<string, GroundingArtifact>;
  loadingTaskIds: Record<string, boolean>;
  errorsByTaskId: Record<string, string>;
  sectionOptions: ReferenceSectionOption[];
  activeTarget: GroundingReferenceTarget | null;
  selectedProject?: { id?: string | null; organization_id?: string | null } | null;
  reportTemplateId?: string | null;
  draftStyles?: FontStyleSettings;
  retrieveSourceDocument: (documentId: string) => Promise<SourceViewerPayload>;
  graphView?: GraphViewController | null;
  onReferenceNavigate?: (
    taskId: string,
    kind: GroundingReferenceKind,
    id: string,
    target?: Partial<GroundingReferenceTarget>
  ) => Promise<boolean | void> | boolean | void;
}

type ReferenceTab = "generated" | "rationale";

interface RationaleSourceLink {
  key: string;
  source: GroundingSourceRecord;
}

interface SourceExpansionOptions {
  preferUnlinked?: boolean;
  sourceKey?: string;
}

interface SourceExpansionTarget {
  sourceId: string;
  sourceKey?: string;
}

const GROUNDING_SOURCE_CATEGORY_LABELS: Record<GroundingSourceCategory, string> = {
  source_excerpt: "Source Excerpt",
  database_query: "Database Query",
  template_instruction: "Template Instruction",
  confirmed_no_data_found: "Confirmed No Data Found",
  tool_call: "Tool Call",
};

const isToolCallSource = (source: GroundingSourceRecord): boolean => {
  const richSource = source as unknown as Record<string, unknown>;
  return Boolean(richSource.tool_call || richSource.tool_name);
};

const getToolCallSummary = (source: GroundingSourceRecord): string => {
  const richSource = source as unknown as Record<string, unknown>;
  const call = richSource.tool_call;
  const name = richSource.tool_name;
  return typeof call === "string" && call.trim()
    ? call.trim()
    : typeof name === "string" && name.trim()
      ? name.trim()
      : "Tool-derived source";
};

const GROUNDING_SOURCE_TYPE_CATEGORIES: Record<string, GroundingSourceCategory> = {
  quoted: "source_excerpt",
  source_passage: "source_excerpt",
  table_row: "source_excerpt",
  literature_result: "source_excerpt",
  other: "source_excerpt",
  computed: "database_query",
  query_result: "database_query",
  terminology_lookup: "database_query",
  template_rule: "template_instruction",
  absence_result: "confirmed_no_data_found",
};

const isGroundingSourceCategory = (value: unknown): value is GroundingSourceCategory =>
  typeof value === "string" && Object.prototype.hasOwnProperty.call(GROUNDING_SOURCE_CATEGORY_LABELS, value);

const getGroundingSourceCategory = (source: GroundingSourceRecord): GroundingSourceCategory => {
  if (isGroundingSourceCategory(source.category)) return source.category;
  return GROUNDING_SOURCE_TYPE_CATEGORIES[source.raw_type || source.type] || "source_excerpt";
};

const getGroundingSourceCategoryLabel = (source: GroundingSourceRecord) => {
  if (source.category_label?.trim()) return source.category_label.trim();
  return GROUNDING_SOURCE_CATEGORY_LABELS[getGroundingSourceCategory(source)];
};

const getSourceDisplayFilename = (filename?: string | null) => {
  const sourceFilename = filename?.trim() || "Unknown source";
  return sourceFilename.replace(/_extracted\.txt$/i, ".pdf");
};

function AbsenceSearchDetail({
  absence,
  onOpenDocument,
}: {
  absence: AbsenceSearchSummary;
  onOpenDocument?: (documentId: string, filename: string) => void;
}) {
  return (
    <div className="reference-absence-detail">
      <p className="reference-absence-title">Searched, nothing found</p>
      {absence.queries.length > 0 && (
        <div className="reference-absence-queries">
          <span className="reference-absence-label">Queries</span>
          <ul className="reference-absence-list">
            {absence.queries.map((query) => (
              <li key={query}>{query}</li>
            ))}
          </ul>
        </div>
      )}
      {absence.documents.length > 0 && (
        <div className="reference-absence-documents">
          <span className="reference-absence-label">Searched documents</span>
          <ul className="reference-absence-doc-list">
            {absence.documents.map((document, index) => {
              const filename = getSourceDisplayFilename(document.filename);
              return (
                <li
                  key={`${document.documentId ?? filename}:${index}`}
                  className="reference-absence-doc-item"
                >
                  <FileText size={12} strokeWidth={2.2} aria-hidden="true" className="reference-absence-doc-icon" />
                  {document.documentId && onOpenDocument ? (
                    <button
                      type="button"
                      onClick={() => onOpenDocument(document.documentId!, filename)}
                      className="reference-absence-doc-button"
                      aria-label={`Open searched document ${filename}`}
                    >
                      {filename}
                    </button>
                  ) : (
                    <span className="reference-absence-doc-name">{filename}</span>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

const GroundingSourceCategoryIcon = ({ category }: { category: GroundingSourceCategory }) => {
  if (category === "database_query") {
    return <Database size={10} strokeWidth={2.4} aria-hidden="true" />;
  }
  if (category === "template_instruction") {
    return <ClipboardList size={10} strokeWidth={2.4} aria-hidden="true" />;
  }
  if (category === "confirmed_no_data_found") {
    return <CircleOff size={10} strokeWidth={2.4} aria-hidden="true" />;
  }
  return <FileText size={10} strokeWidth={2.4} aria-hidden="true" />;
};

const preprocessGroundingMarkdown = preprocessGroundingMarkerRuns;

const SourceChip = ({
  source,
  expanded,
  highlighted = false,
  onClick,
  onViewSourceDocument,
}: {
  source: GroundingSourceRecord;
  expanded: boolean;
  highlighted?: boolean;
  onClick: () => void;
  onViewSourceDocument: (source: GroundingSourceRecord) => Promise<void>;
}) => {
  const sourceFilename = getSourceDisplayFilename(source.source);
  const hasFileTypeIcon = hasMappedFileTypeIcon(sourceFilename);
  const canViewDocument = Boolean(source.document_id);
  const [viewing, setViewing] = useState(false);

  const handlePreviewClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (!canViewDocument || viewing) return;

    setViewing(true);
    try {
      await onViewSourceDocument(source);
    } catch (error) {
      console.error("Unable to open source file preview.", error);
      message.error(error instanceof Error ? error.message : "Unable to open source file preview.");
    } finally {
      setViewing(false);
    }
  };

  return (
    <div
      className={`reference-evidence-chip ${expanded ? "reference-evidence-chip--active" : ""} ${
        highlighted ? "reference-evidence-chip--highlighted" : ""
      }`}
      data-source-chip-id={source.id}
    >
      <button
        type="button"
        onClick={onClick}
        className="reference-evidence-chip-main"
        aria-expanded={expanded}
        aria-label={`${expanded ? "Collapse" : "Expand"} source ${sourceFilename}`}
      >
        <span
          className={`reference-evidence-chip-icon ${hasFileTypeIcon ? "reference-evidence-chip-icon--file-type" : ""}`}
        >
          <FileTypeIcon filename={sourceFilename} imageClassName="reference-evidence-chip-icon-image" />
        </span>
        <span className="reference-evidence-chip-label">
          <span className="reference-evidence-chip-name">{sourceFilename}</span>
        </span>
      </button>
      {canViewDocument ? (
        <Tooltip title={viewing ? "Opening source file..." : "Open source file"}>
          <button
            type="button"
            className="reference-evidence-preview-button"
            onClick={handlePreviewClick}
            disabled={viewing}
            aria-label={`Open source file for ${sourceFilename}`}
          >
            <Eye size={11} strokeWidth={2.2} aria-hidden="true" />
            <span>{viewing ? "Opening..." : "Open"}</span>
          </button>
        </Tooltip>
      ) : null}
      <button
        type="button"
        onClick={onClick}
        className="reference-evidence-chip-arrow-button"
        aria-expanded={expanded}
        aria-label={`${expanded ? "Collapse" : "Expand"} source ${sourceFilename}`}
      >
        <ChevronRight className="reference-evidence-chip-arrow" size={13} strokeWidth={2.5} aria-hidden="true" />
      </button>
    </div>
  );
};

const SourceCard = ({
  source,
  highlighted,
  onViewSourceDocument,
  onGroundingLinkClick,
}: {
  source: GroundingSourceRecord;
  highlighted: boolean;
  onViewSourceDocument: (source: GroundingSourceRecord) => Promise<void>;
  onGroundingLinkClick?: (kind: "source" | "rationale", id: string) => void;
}) => {
  const { isSuperAdmin } = useAuth();
  const handleLinkClick: (kind: "source" | "rationale", id: string) => void =
    onGroundingLinkClick ?? (() => {});
  const [viewing, setViewing] = useState(false);
  const [viewError, setViewError] = useState("");
  // Workspace parity: multi-span excerpts, spreadsheet value
  // projection and the cached-cell locator cleanup all come from the shared
  // presentation module so the add-in and the workspace read identically.
  const excerpt = sourceExcerptDisplayText(source);
  const sourceLocator = sourceLocatorDisplayText(source.locator || "");
  const isAbsenceSearch = isAbsenceSearchSource(source);
  const absence = absenceSearchSummary(source);
  const absenceTerms = isAbsenceSearch ? absenceSearchTerms(source) : [];
  const category = getGroundingSourceCategory(source);
  const categoryLabel = getGroundingSourceCategoryLabel(source);
  const isSourceExcerpt = category === "source_excerpt";
  const isToolDerived = Boolean(isSuperAdmin && isToolCallSource(source));
  const hasUnresolvedFile = isUnresolvedFileSource(source);
  const canViewDocument = Boolean(source.document_id);
  const fileExtension = getSourceFileExtension(source);
  const isTabularEvidence =
    fileExtension === "csv" || fileExtension === "xls" || fileExtension === "xlsx" || fileExtension === "excel";

  const handleViewDocument = async () => {
    if (!canViewDocument || viewing) return;
    setViewing(true);
    setViewError("");
    try {
      await onViewSourceDocument(source);
    } catch (error: unknown) {
      setViewError(error instanceof Error ? error.message : "Unable to open the source document.");
    } finally {
      setViewing(false);
    }
  };

  const handleOpenAbsenceDocument = async (documentId: string, filename: string) => {
    try {
      await onViewSourceDocument({
        ...source,
        document_id: documentId,
        filename,
      });
    } catch (error: unknown) {
      setViewError(error instanceof Error ? error.message : "Unable to open the source document.");
    }
  };

  return (
    <section
      className={`reference-card reference-card--evidence ${
        isTabularEvidence ? "reference-card--tabular-evidence" : ""
      } ${highlighted ? "reference-card--active" : ""}`}
    >
      <div className="reference-source-text-block">
        <div className="reference-source-timeline-content">
          <div className="reference-source-timeline-rail">
            <div className="reference-source-timeline-item">
              <span className="reference-source-timeline-icon" aria-hidden="true">
                <Quote size={14} strokeWidth={2.2} aria-hidden="true" />
              </span>
              <span className="reference-excerpt-label-row">
                <span
                  className={`reference-status reference-status--category ${
                    isSourceExcerpt ? "reference-status--category-source" : "reference-status--category-neutral"
                  }`}
                >
                  <GroundingSourceCategoryIcon category={category} />
                  {categoryLabel}
                </span>
                {source.verified ? (
                  <Tooltip title="Exact source text verified">
                    <span
                      className="reference-status reference-status--verified"
                      aria-label="Verbatim: exact source text verified"
                    >
                      <CheckCircle2 size={10} strokeWidth={2.2} aria-hidden="true" /> Verbatim
                    </span>
                  </Tooltip>
                ) : null}
                {viewing ? <span className="reference-excerpt-opening">Opening</span> : null}
              </span>
              {isToolDerived && (
                <div className="reference-source-timeline-item" style={{ marginTop: "0.25rem" }}>
                  <span className="reference-source-timeline-icon" aria-hidden="true">
                    <Terminal size={14} strokeWidth={2.2} aria-hidden="true" />
                  </span>
                  <span className="reference-source-timeline-line">
                    <span className="reference-source-timeline-label">Tool Call</span>
                    <span className="reference-source-timeline-separator" aria-hidden="true">
                      {" · "}
                    </span>
                    <span className="reference-source-timeline-text">
                      {getToolCallSummary(source)}
                    </span>
                  </span>
                </div>
              )}
              <div
                className="reference-source-timeline-line reference-excerpt"
                aria-busy={viewing || undefined}
              >
                {absence ? (
                  <AbsenceSearchDetail
                    absence={absence}
                    onOpenDocument={handleOpenAbsenceDocument}
                  />
                ) : excerpt ? (
                  <div className="reference-excerpt-markdown">
                    <GroundingMarkdown content={excerpt} onGroundingLinkClick={handleLinkClick} />
                  </div>
                ) : isAbsenceSearch ? (
                  <span className="reference-source-timeline-text">
                    {absenceTerms.length
                      ? `Searched for ${absenceTerms.join(", ")}`
                      : "Searched, nothing found"}
                    {" — No matching evidence in this project"}
                  </span>
                ) : (
                  <span className="reference-no-excerpt">No excerpt supplied</span>
                )}
              </div>
              {hasUnresolvedFile && (
                <p className="reference-unresolved-file-warning">
                  File source could not be linked to a project document.
                </p>
              )}
            </div>
            {viewError && <div className="reference-errors reference-errors--source-view">{viewError}</div>}
            {sourceLocator && !absence?.queries?.length && (
              <div className="reference-source-timeline-item">
                <span className="reference-source-timeline-icon" aria-hidden="true">
                  <FileSearch size={14} strokeWidth={2.2} aria-hidden="true" />
                </span>
                <span className="reference-source-timeline-line">
                  <span className="reference-source-timeline-label">Location</span>
                  <span className="reference-source-timeline-separator" aria-hidden="true">
                    {"\u00b7"}
                  </span>
                  <span className="reference-source-timeline-text">{sourceLocator}</span>
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {source.verification_errors?.length > 0 && (
        <div className="reference-errors">{source.verification_errors.join(" - ")}</div>
      )}
    </section>
  );
};

const RationaleCard = ({
  rationale,
  rationaleNumber,
  linkedSources,
  highlighted,
  expandedSourceKeys,
  onSourceToggle,
  onSourceReferenceClick,
  onReferenceClick,
  onViewSourceDocument,
  rationaleTypographyStyle,
}: {
  rationale: GroundingRationaleRecord;
  rationaleNumber: number;
  linkedSources: RationaleSourceLink[];
  highlighted: boolean;
  expandedSourceKeys: Set<string>;
  onSourceToggle: (sourceKey: string, sourceId: string) => void;
  onSourceReferenceClick: (sourceId: string, rationaleId: string, sourceIds: string[]) => void;
  onReferenceClick: () => void;
  onViewSourceDocument: (source: GroundingSourceRecord) => Promise<void>;
  rationaleTypographyStyle?: React.CSSProperties;
}) => (
  <section className={`reference-card reference-card--inference ${highlighted ? "reference-card--active" : ""}`}>
    <div className="reference-inference-content-row">
      <p className="reference-rationale" style={rationaleTypographyStyle}>
        <button
          type="button"
          onClick={onReferenceClick}
          className="reference-inference-id"
          aria-label={`Go to ${getGroundingReferenceDisplayId(rationale.id)}`}
        >
          {rationaleNumber}
        </button>
        <Brain
          className="reference-rationale-brain"
          strokeWidth={2.4}
          role="img"
          aria-label="AI generated rationale"
        />
        {rationale.rationale}
      </p>
    </div>

    {linkedSources.length > 0 && (
      <div className="reference-linked-evidence">
        {linkedSources.map(({ key, source }) => {
          const isExpanded = expandedSourceKeys.has(key);
          return (
            <div key={key} className="reference-evidence-stack">
              <div className="reference-evidence-row">
                <SourceChip
                  source={source}
                  expanded={isExpanded}
                  onViewSourceDocument={onViewSourceDocument}
                  onClick={() => {
                    onSourceToggle(key, source.id);
                    onSourceReferenceClick(
                      source.id,
                      rationale.id,
                      linkedSources.map((linkedItem) => linkedItem.source.id)
                    );
                  }}
                />
              </div>
              {isExpanded && (
                <div data-source-id={source.id} className="reference-source-detail">
                  <SourceCard source={source} highlighted={highlighted} onViewSourceDocument={onViewSourceDocument} />
                </div>
              )}
            </div>
          );
        })}
      </div>
    )}
  </section>
);

const OtherSourcesBlock = ({
  sources,
  highlightedSourceId,
  highlightedSourceIds,
  expandedSourceKeys,
  onSourceToggle,
  onSourceReferenceClick,
  onViewSourceDocument,
}: {
  sources: GroundingSourceRecord[];
  highlightedSourceId: string | null;
  highlightedSourceIds: Set<string>;
  expandedSourceKeys: Set<string>;
  onSourceToggle: (sourceKey: string, sourceId: string) => void;
  onSourceReferenceClick: (sourceId: string) => void;
  onViewSourceDocument: (source: GroundingSourceRecord) => Promise<void>;
}) => {
  if (sources.length === 0) return null;
  const isHighlighted = sources.some((item) => item.id === highlightedSourceId || highlightedSourceIds.has(item.id));

  return (
    <section
      className={`reference-unlinked-sources ${isHighlighted ? "reference-unlinked-sources--active" : ""}`}
      data-unlinked-sources-block="true"
    >
      <div className="reference-section-label reference-section-label--inline">Other sources</div>
      <div className="reference-linked-evidence">
        {sources.map((item) => {
          const key = `unlinked:${item.id}`;
          const isExpanded = expandedSourceKeys.has(key);
          return (
            <div key={key} className="reference-evidence-stack">
              <div className="reference-evidence-row">
                <SourceChip
                  source={item}
                  expanded={isExpanded}
                  highlighted={highlightedSourceId === item.id || highlightedSourceIds.has(item.id)}
                  onViewSourceDocument={onViewSourceDocument}
                  onClick={() => {
                    onSourceToggle(key, item.id);
                    onSourceReferenceClick(item.id);
                  }}
                />
              </div>
              {isExpanded && (
                <div data-source-id={item.id} className="reference-source-detail">
                  <SourceCard
                    source={item}
                    highlighted={highlightedSourceId === item.id || highlightedSourceIds.has(item.id)}
                    onViewSourceDocument={onViewSourceDocument}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
};

const ReferencePage = ({
  groundingByTaskId,
  loadingTaskIds,
  errorsByTaskId,
  sectionOptions,
  activeTarget,
  selectedProject,
  reportTemplateId,
  draftStyles,
  retrieveSourceDocument,
  onReferenceNavigate,
  graphView,
}: ReferencePageProps) => {
  const { selectedEnvironment } = useEnvironment();
  const { tokens, isSuperAdmin, organizationId } = useAuth();
  const [selectedSectionId, setSelectedSectionId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<ReferenceTab>("generated");
  const [highlightedId, setHighlightedId] = useState<string | null>(null);
  const [highlightedSourceIds, setHighlightedSourceIds] = useState<Set<string>>(new Set());
  const [expandedSourceKeys, setExpandedSourceKeys] = useState<Set<string>>(new Set());
  const [rationaleEvidenceDetailMode, setRationaleEvidenceDetailMode] = useState<RationaleEvidenceDetailMode>(
    getStoredRationaleEvidenceDetailMode
  );
  const scrollRootRef = useRef<HTMLDivElement | null>(null);
  const highlightTimeoutRef = useRef<number | null>(null);
  const highlightRestartTimeoutRef = useRef<number | null>(null);

  const selectedOption = useMemo(() => {
    if (!sectionOptions.length) return null;
    return sectionOptions.find((option) => option.sectionId === selectedSectionId) || sectionOptions[0];
  }, [sectionOptions, selectedSectionId]);

  const selectedArtifact =
    selectedOption?.enabled && selectedOption.taskId ? groundingByTaskId[selectedOption.taskId] || null : null;
  const hasLoadingTasks = Object.values(loadingTaskIds).some(Boolean);
  const selectedError = selectedOption?.taskId ? errorsByTaskId[selectedOption.taskId] : "";
  const selectedProjectId = selectedProject?.id || null;
  const activeEvidenceDetailMode: RationaleEvidenceDetailMode = isSuperAdmin ? rationaleEvidenceDetailMode : "less";

  const rationaleTypographyStyle = useMemo<React.CSSProperties>(
    () => ({
      ...(draftStyles?.fontColor ? { color: draftStyles.fontColor } : {}),
      ...(draftStyles?.fontColor
        ? ({ "--reference-rationale-accent": draftStyles.fontColor } as React.CSSProperties)
        : {}),
      fontStyle: "normal",
      fontWeight: 400,
    }),
    [draftStyles]
  );

  const allSources = useMemo(() => selectedArtifact?.sources || [], [selectedArtifact]);

  const visibleSources = useMemo(() => {
    return activeEvidenceDetailMode === "more" ? allSources : allSources.filter(shouldShowSourceInLessRationaleDetail);
  }, [activeEvidenceDetailMode, allSources]);

  const visibleSourceById = useMemo(() => {
    const lookup = new Map<string, GroundingSourceRecord>();
    visibleSources.forEach((source) => {
      lookup.set(source.id, source);
    });
    return lookup;
  }, [visibleSources]);

  const otherSourceIds = useMemo(() => {
    const ids = new Set<string>();
    if (!selectedArtifact) return ids;

    buildGroundingReferenceBlocks(selectedArtifact.task_id, selectedArtifact.markdown).forEach((block) => {
      if (block.sourceOnly) {
        block.sourceIds.forEach((sourceId) => {
          ids.add(sourceId);
        });
      }
    });

    return ids;
  }, [selectedArtifact]);

  const otherSources = useMemo(
    () => allSources.filter((source) => otherSourceIds.has(source.id)),
    [allSources, otherSourceIds]
  );

  const getSourceKeysForSource = (
    artifact: GroundingArtifact | null,
    sourceId: string,
    options: SourceExpansionOptions = {}
  ) => {
    if (!artifact) return [];

    const artifactSourceIds = new Set(artifact.sources.map((source) => source.id));
    if (!artifactSourceIds.has(sourceId)) return [];

    if (options.sourceKey) {
      return [options.sourceKey];
    }

    const unlinkedKey = `unlinked:${sourceId}`;
    if (options.preferUnlinked) {
      return [unlinkedKey];
    }

    const relationshipKeys = artifact.rationales.flatMap((rationale) =>
      rationale.source_ids.flatMap((linkedSourceId, index) =>
        linkedSourceId === sourceId ? [`${rationale.id}:${linkedSourceId}:${index}`] : []
      )
    );

    return relationshipKeys.length > 0 ? relationshipKeys : [unlinkedKey];
  };

  const expandSourceBySourceId = (
    sourceId: string,
    artifact: GroundingArtifact | null = selectedArtifact,
    options: SourceExpansionOptions = {}
  ) => {
    const keysToOpen = getSourceKeysForSource(artifact, sourceId, options);
    if (keysToOpen.length === 0) return;

    setExpandedSourceKeys((current) => {
      const next = new Set(current);
      keysToOpen.forEach((key) => {
        next.add(key);
      });
      return next;
    });
  };

  const getFirstVisibleSourceId = (sourceIds: string[] = []) =>
    sourceIds.find((sourceId) => visibleSourceById.has(sourceId)) || sourceIds[0] || null;

  const getFirstSourceTargetForRationale = (
    rationaleId: string,
    artifact: GroundingArtifact | null = selectedArtifact,
    preferredSourceIds: string[] = []
  ): SourceExpansionTarget | null => {
    const rationale = artifact?.rationales.find((item) => item.id === rationaleId);
    const rationaleSourceIds = rationale?.source_ids || [];
    const sourceId = getFirstVisibleSourceId(preferredSourceIds) || getFirstVisibleSourceId(rationaleSourceIds);
    if (!sourceId) return null;

    const sourceIndex = rationaleSourceIds.indexOf(sourceId);
    return {
      sourceId,
      sourceKey: `${rationaleId}:${sourceId}:${sourceIndex >= 0 ? sourceIndex : 0}`,
    };
  };

  const getSourceToExpandForTarget = (
    target: GroundingReferenceTarget,
    artifact: GroundingArtifact | null = selectedArtifact
  ): SourceExpansionTarget | null => {
    if (target.kind === "source") {
      const sourceId = getFirstVisibleSourceId(target.sourceIds || []) || target.id;
      return sourceId ? { sourceId } : null;
    }

    return getFirstSourceTargetForRationale(target.id, artifact, target.sourceIds || []);
  };

  const scrollToOtherSource = (sourceId: string) => {
    const otherSourcesBlock = scrollRootRef.current?.querySelector(`[data-unlinked-sources-block="true"]`);
    const target = otherSourcesBlock?.querySelector(`[data-source-id="${sourceId}"]`) || otherSourcesBlock;
    target?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const highlightTemporarily = (id: string, sourceIds: string[] = []) => {
    if (highlightTimeoutRef.current) {
      window.clearTimeout(highlightTimeoutRef.current);
    }
    if (highlightRestartTimeoutRef.current) {
      window.clearTimeout(highlightRestartTimeoutRef.current);
    }

    setHighlightedId(null);
    setHighlightedSourceIds(new Set());

    highlightRestartTimeoutRef.current = window.setTimeout(() => {
      setHighlightedId(id);
      setHighlightedSourceIds(new Set(sourceIds));
      highlightRestartTimeoutRef.current = null;
      highlightTimeoutRef.current = window.setTimeout(() => {
        setHighlightedId(null);
        setHighlightedSourceIds(new Set());
        highlightTimeoutRef.current = null;
      }, 3000);
    }, 0);
  };

  const navigateToDocumentReference = (
    kind: GroundingReferenceKind,
    id: string,
    target?: Partial<GroundingReferenceTarget>
  ) => {
    if (!selectedArtifact?.task_id || !onReferenceNavigate) return;
    void onReferenceNavigate(selectedArtifact.task_id, kind, id, target);
  };

  const viewSourceDocument = async (source: GroundingSourceRecord) => {
    const documentId = source.document_id;
    if (!documentId) {
      throw new Error("This source does not include a document id.");
    }

    closeActiveViewerDialog();
    const status = beginViewerOpenStatus(sourceDocumentDisplayName(source));
    try {
      const payload = await retrieveSourceDocument(documentId);
      const payloadKey = await storeSourceViewerPayload({
        ...payload,
        document_id: documentId,
        project_id: selectedProjectId || (source as any).project_id || null,
        report_template_id: reportTemplateId || (source as any).report_template_id || null,
        organization_id: organizationId || selectedProject?.organization_id || null,
        api_base_url: selectedEnvironment?.url || null,
        access_token: tokens?.accessToken || null,
        refresh_token: tokens?.refreshToken || null,
        is_super_admin: isSuperAdmin,
        evidenceTarget: {
          locator: source.locators ?? null,
          excerpt: source.excerpt,
          sourceType: source.type,
          fileType: source.file_type,
        },
      });
      await openSourceViewerDialog(payloadKey, { onClosingStatus: status.onClosingStatus });
    } catch (error) {
      throw status.describeFailure(error);
    } finally {
      status.done();
    }
  };

  useEffect(() => {
    return () => {
      if (highlightTimeoutRef.current) {
        window.clearTimeout(highlightTimeoutRef.current);
      }
      if (highlightRestartTimeoutRef.current) {
        window.clearTimeout(highlightRestartTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const syncRationaleEvidenceDetailMode = (event?: Event) => {
      const detail = event instanceof CustomEvent ? event.detail : null;
      setRationaleEvidenceDetailMode(
        isRationaleEvidenceDetailMode(detail) ? detail : getStoredRationaleEvidenceDetailMode()
      );
    };

    window.addEventListener(RATIONALE_EVIDENCE_DETAIL_CHANGED_EVENT, syncRationaleEvidenceDetailMode);
    return () => {
      window.removeEventListener(RATIONALE_EVIDENCE_DETAIL_CHANGED_EVENT, syncRationaleEvidenceDetailMode);
    };
  }, []);

  useEffect(() => {
    if (selectedSectionId && sectionOptions.some((option) => option.sectionId === selectedSectionId)) {
      return undefined;
    }

    const firstEnabled = sectionOptions.find((option) => option.enabled);
    const firstOption = firstEnabled || sectionOptions[0] || null;
    setSelectedSectionId(firstOption?.sectionId || null);
  }, [sectionOptions, selectedSectionId]);

  useEffect(() => {
    if (!activeTarget) return undefined;

    const owningSection = sectionOptions.find((option) => option.taskId === activeTarget.taskId);
    if (owningSection) {
      setSelectedSectionId(owningSection.sectionId);
    }
    setActiveTab("rationale");
    const activeArtifact = groundingByTaskId[activeTarget.taskId] || selectedArtifact;

    if (activeTarget.kind === "source" && activeTarget.sourceOnly) {
      const sourceIdToExpand = getFirstVisibleSourceId(activeTarget.sourceIds || []) || activeTarget.id;
      expandSourceBySourceId(sourceIdToExpand, activeArtifact, { preferUnlinked: true });
      highlightTemporarily(activeTarget.id, activeTarget.sourceIds || []);

      window.setTimeout(() => {
        scrollToOtherSource(sourceIdToExpand);
      }, 80);
      return undefined;
    }

    const sourceToExpand = getSourceToExpandForTarget(activeTarget, activeArtifact);
    if (sourceToExpand) {
      expandSourceBySourceId(sourceToExpand.sourceId, activeArtifact, { sourceKey: sourceToExpand.sourceKey });
    }
    highlightTemporarily(activeTarget.id, activeTarget.sourceIds || []);

    window.setTimeout(() => {
      const targetSelector =
        activeTarget.kind === "source"
          ? `[data-source-id="${sourceToExpand?.sourceId || activeTarget.id}"]`
          : `[data-rationale-id="${activeTarget.id}"]`;
      const target = scrollRootRef.current?.querySelector(targetSelector);
      target?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 80);
  }, [activeTarget, activeTarget?.activationKey, sectionOptions]);

  const toggleSourceExpansion = (sourceKey: string, sourceId: string) => {
    setExpandedSourceKeys((current) => {
      const next = new Set(current);
      if (next.has(sourceKey)) {
        next.delete(sourceKey);
      } else {
        next.add(sourceKey);
      }
      return next;
    });
    highlightTemporarily(sourceId);
  };

  const selectSource = (sourceId: string, shouldNavigateDocument = false) => {
    setActiveTab("rationale");
    expandSourceBySourceId(sourceId, selectedArtifact, { preferUnlinked: true });
    highlightTemporarily(sourceId);
    if (shouldNavigateDocument) {
      navigateToDocumentReference("source", sourceId, { sourceOnly: true });
    }
    window.setTimeout(() => {
      scrollToOtherSource(sourceId);
    }, 80);
  };

  const selectRationale = (rationaleId: string, shouldNavigateDocument = false) => {
    setActiveTab("rationale");
    const firstSource = getFirstSourceTargetForRationale(rationaleId);
    if (firstSource) {
      expandSourceBySourceId(firstSource.sourceId, selectedArtifact, { sourceKey: firstSource.sourceKey });
    }
    highlightTemporarily(rationaleId);
    if (shouldNavigateDocument) {
      navigateToDocumentReference("rationale", rationaleId);
    }
    window.setTimeout(() => {
      const target = scrollRootRef.current?.querySelector(`[data-rationale-id="${rationaleId}"]`);
      target?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 80);
  };

  const handleGeneratedContentAnchorClick = (kind: "source" | "rationale", id: string) => {
    if (kind === "source") {
      selectSource(id, true);
    } else {
      selectRationale(id, true);
    }
  };

  const generatedContentPreviewBlocks = useMemo(
    () => prepareGeneratedContentPreview(selectedArtifact?.markdown || ""),
    [selectedArtifact?.markdown]
  );

  const renderGeneratedMarkdown = (content: string) => (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        ...getGroundingMarkdownComponents({ onGroundingLinkClick: handleGeneratedContentAnchorClick }),
        ...getTodoTextComponents(),
      }}
    >
      {preprocessGroundingMarkdown(content)}
    </ReactMarkdown>
  );

  return (
    <div className="reference-page taskpane-compact">
      <header className="reference-header">
        {(sectionOptions.length > 0 || graphView) && (
          <div className="reference-header-controls">
            {sectionOptions.length > 0 && (
              <Select
                className="reference-section-select settings_form_control_full"
                popupClassName="reference-section-select-popup"
                value={selectedOption?.sectionId || ""}
                onChange={setSelectedSectionId}
                size="small"
                options={sectionOptions.map((option) => ({
                  label: option.label,
                  value: option.sectionId,
                  disabled: !option.enabled,
                  title: option.disabledReason || option.label,
                }))}
              />
            )}
            {graphView && (
              <Tooltip
                title={
                  graphView.opening
                    ? "Opening Sections Graph..."
                    : graphView.disabled
                    ? "Section generation is in progress"
                    : "View Sections Graph"
                }
              >
                <span className="reference-graph-button-wrap">
                  <button
                    type="button"
                    className="section_generator_panel_button_default reference-graph-button"
                    onClick={graphView.disabled ? undefined : graphView.open}
                    aria-label={graphView.opening ? "Opening Sections Graph" : "View Sections Graph"}
                    disabled={graphView.disabled}
                  >
                    {graphView.opening ? <LoadingOutlined /> : <GitGraph size={15} strokeWidth={1.8} />}
                  </button>
                </span>
              </Tooltip>
            )}
          </div>
        )}
      </header>

      {selectedArtifact && (
        <div className="reference-tabs">
          <button
            type="button"
            onClick={() => setActiveTab("generated")}
            className={`reference-tab ${activeTab === "generated" ? "reference-tab--active" : ""}`}
          >
            Generated Content
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("rationale")}
            className={`reference-tab ${activeTab === "rationale" ? "reference-tab--active" : ""}`}
          >
            Rationale
            <span>{selectedArtifact.rationales.length}</span>
          </button>
        </div>
      )}

      <main ref={scrollRootRef} className="reference-body">
        {hasLoadingTasks && (
          <div className="reference-notice reference-notice--loading">Loading grounding records...</div>
        )}

        {selectedError && <div className="reference-notice reference-notice--warning">{selectedError}</div>}

        {selectedOption && !selectedOption.enabled && !selectedError && (
          <div className="reference-empty">
            <div>No references available</div>
            <p>{selectedOption.disabledReason}</p>
          </div>
        )}

        {!selectedOption && !hasLoadingTasks && (
          <div className="reference-empty">
            <div>No source references yet</div>
            <p>Source references will appear here after generation completes.</p>
          </div>
        )}

        {selectedArtifact && activeTab === "generated" && (
          <div className="reference-generated-content">
            <GeneratedContentPreview blocks={generatedContentPreviewBlocks} renderMarkdown={renderGeneratedMarkdown} />
          </div>
        )}

        {selectedArtifact && activeTab === "rationale" && (
          <div className="reference-card-list">
            {selectedArtifact.rationales.length === 0 && otherSources.length === 0 ? (
              <div className="reference-empty reference-empty--compact">No rationale or source records.</div>
            ) : (
              <>
                {selectedArtifact.rationales.map((rationale, index) => {
                  const linkedSources = rationale.source_ids
                    .map((sourceId, index) => {
                      const source = visibleSourceById.get(sourceId);
                      return source ? { key: `${rationale.id}:${sourceId}:${index}`, source } : null;
                    })
                    .filter((value): value is RationaleSourceLink => value !== null);

                  return (
                    <div key={rationale.id} data-rationale-id={rationale.id}>
                      <RationaleCard
                        rationale={rationale}
                        rationaleNumber={index + 1}
                        linkedSources={linkedSources}
                        highlighted={highlightedId === rationale.id}
                        expandedSourceKeys={expandedSourceKeys}
                        rationaleTypographyStyle={rationaleTypographyStyle}
                        onViewSourceDocument={viewSourceDocument}
                        onSourceToggle={toggleSourceExpansion}
                        onSourceReferenceClick={(sourceId, rationaleId, sourceIds) => {
                          highlightTemporarily(sourceId);
                          navigateToDocumentReference("source", sourceId, {
                            kind: "rationale",
                            id: rationaleId,
                            sourceIds,
                          });
                        }}
                        onReferenceClick={() => {
                          highlightTemporarily(rationale.id);
                          navigateToDocumentReference("rationale", rationale.id, {
                            sourceIds: linkedSources.map((linkedItem) => linkedItem.source.id),
                          });
                        }}
                      />
                    </div>
                  );
                })}
                <OtherSourcesBlock
                  sources={otherSources}
                  highlightedSourceId={highlightedId}
                  highlightedSourceIds={highlightedSourceIds}
                  expandedSourceKeys={expandedSourceKeys}
                  onViewSourceDocument={viewSourceDocument}
                  onSourceToggle={toggleSourceExpansion}
                  onSourceReferenceClick={(sourceId) => {
                    highlightTemporarily(sourceId);
                    navigateToDocumentReference("source", sourceId, { sourceOnly: true });
                  }}
                />
              </>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default ReferencePage;
